'use client';

import { useState, useEffect, useRef } from 'react';
import { useReveal } from '@/hooks/use-reveal';
import { Award, Medal, ShieldCheck, Star, Globe2, Crown } from 'lucide-react';

interface Distinction {
  year: string;
  award: string;
  organization: string;
  icon: typeof Award;
  detail?: string;
}

const DISTINCTIONS: Distinction[] = [
  {
    year: '2024',
    award: "Maître d'Art",
    organization: 'Institut de France',
    icon: Crown,
    detail: "Titre honorifique décerné à un artisan vivant, pour l'ensemble de son œuvre.",
  },
  {
    year: '2023',
    award: 'Entreprise du Patrimoine Vivant',
    organization: 'État Français',
    icon: ShieldCheck,
    detail: "Label d'État distinguant les maisons au savoir-faire artisanal d'exception.",
  },
  {
    year: '2022',
    award: 'Prix de la Maroquinerie',
    organization: 'Fédération de la Mode',
    icon: Medal,
    detail: 'Récompensée pour la création « Manteau-Cabas Numéro 1 », pièce unique.',
  },
  {
    year: '2021',
    award: 'Sélection Trésors de Paris',
    organization: 'Ville de Paris',
    icon: Star,
    detail: "Reconnaissance des artisans parisiens d'exception, décernée tous les deux ans.",
  },
  {
    year: '2019',
    award: 'Membre Gréement',
    organization: 'Comité Colbert',
    icon: Globe2,
    detail: "Admission au cercle des maisons de luxe françaises, après 18 mois d'audit.",
  },
  {
    year: '2017',
    award: 'Lauréate Jeune Création',
    organization: 'Maison des Artisans',
    icon: Award,
    detail: 'Prix de la première pièce, décerné à la fondatrice pour son sac « Selle ».',
  },
];

export default function Distinctions() {
  const { ref, visible } = useReveal();
  const [activeIdx, setActiveIdx] = useState(0);
  const autoRotate = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!visible) return;
    autoRotate.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % DISTINCTIONS.length);
    }, 5500);
    return () => {
      if (autoRotate.current) clearInterval(autoRotate.current);
    };
  }, [visible]);

  return (
    <section
      id="distinctions"
      ref={ref}
      className="relative py-20 md:py-28 bg-[#2B1810] overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 50% 50%, rgba(201, 169, 97, 0.12) 0%, transparent 60%)',
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(248, 241, 228, 0.6) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container mx-auto px-6 relative">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className={`reveal ${visible ? 'visible' : ''} mb-5`}>
            <div className="flex items-center justify-center gap-4">
              <div className="w-10 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] font-serif-display text-xs tracking-[0.4em] uppercase">
                Distinctions
              </span>
              <div className="w-10 h-px bg-[#C9A961]" />
            </div>
          </div>
          <h2
            className={`reveal ${visible ? 'visible' : ''} font-display text-[#F8F1E4] text-3xl md:text-4xl lg:text-5xl font-medium leading-tight mb-4`}
          >
            Reconnue par les <span className="italic text-luxe-gold">Plus Exigeants</span>
          </h2>
          <p
            className={`reveal ${visible ? 'visible' : ''} text-[#F8F1E4]/65 text-sm md:text-base font-serif-display font-light leading-relaxed`}
          >
            Les marques d'excellence ne s'achètent pas. Elles se méritent, se vérifient,
            se décernent — par des institutions dont la sévérité fait la valeur.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-10">
          <div
            key={activeIdx}
            className="relative bg-[#1A0E08]/60 border border-[#C9A961]/30 p-8 md:p-12 animate-[cinematic-fade_0.7s_ease-out]"
          >
            <div className="absolute top-3 left-3 w-7 h-7 border-l border-t border-[#C9A961]/60" />
            <div className="absolute top-3 right-3 w-7 h-7 border-r border-t border-[#C9A961]/60" />
            <div className="absolute bottom-3 left-3 w-7 h-7 border-l border-b border-[#C9A961]/60" />
            <div className="absolute bottom-3 right-3 w-7 h-7 border-r border-b border-[#C9A961]/60" />

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="shrink-0 relative">
                <div className="absolute inset-0 rounded-full bg-[#C9A961]/20 blur-2xl animate-pulse" />
                <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full border-2 border-[#C9A961] flex items-center justify-center bg-[#1A0E08]">
                  {(() => {
                    const Icon = DISTINCTIONS[activeIdx].icon;
                    return <Icon size={36} strokeWidth={1.25} className="text-[#C9A961]" />;
                  })()}
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-3 mb-3 justify-center md:justify-start">
                  <span className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-serif-display">
                    {DISTINCTIONS[activeIdx].year}
                  </span>
                  <div className="w-8 h-px bg-[#C9A961]/40" />
                  <span className="text-[#F8F1E4]/60 text-[10px] tracking-[0.3em] uppercase font-serif-display">
                    {DISTINCTIONS[activeIdx].organization}
                  </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#F8F1E4] mb-3 tracking-wide">
                  {DISTINCTIONS[activeIdx].award}
                </h3>
                {DISTINCTIONS[activeIdx].detail && (
                  <p className="text-[#F8F1E4]/65 text-sm md:text-base font-serif-display font-light italic leading-relaxed max-w-2xl">
                    {DISTINCTIONS[activeIdx].detail}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 flex-wrap max-w-2xl mx-auto">
          {DISTINCTIONS.map((d, idx) => (
            <button
              key={d.award}
              onClick={() => setActiveIdx(idx)}
              className={`group flex items-center gap-2 px-3 py-2 transition-all duration-400 ${
                idx === activeIdx ? 'opacity-100' : 'opacity-50 hover:opacity-80'
              }`}
              aria-label={`${d.award} — ${d.year}`}
            >
              <div
                className={`transition-all duration-500 ${
                  idx === activeIdx
                    ? 'w-10 h-px bg-[#C9A961]'
                    : 'w-5 h-px bg-[#C9A961]/30 group-hover:bg-[#C9A961]/60'
                }`}
              />
              <span
                className={`text-[9px] tracking-[0.25em] uppercase font-serif-display transition-colors duration-500 ${
                  idx === activeIdx ? 'text-[#C9A961]' : 'text-[#F8F1E4]/40 group-hover:text-[#F8F1E4]/70'
                }`}
              >
                {d.year}
              </span>
            </button>
          ))}
        </div>

        <div
          className={`reveal ${visible ? 'visible' : ''} mt-16 pt-10 border-t border-[#C9A961]/15 flex flex-wrap items-center justify-center gap-8 md:gap-14 text-center`}
        >
          {[
            { value: '25', label: "Ans d'Atelier" },
            { value: '6', label: 'Distinctions' },
            { value: '4 500+', label: 'Clients Confidents' },
            { value: '1/1', label: 'Pièce Unique' },
            { value: '100%', label: 'Cuir Véritable' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-display text-2xl md:text-3xl text-[#C9A961] font-medium tracking-wide">
                {stat.value}
              </div>
              <div className="text-[#F8F1E4]/55 text-[10px] tracking-[0.25em] uppercase font-serif-display mt-1.5">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
